import {
  InnerContentItem,
  List,
  ListItem,
  OuterContentItem,
  SubHeading,
  SummaryMessage,
  Text,
} from "@certn/cascadia-react";
import { usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CategoryType } from "./DocumentField";
import { getDocumentsPoints } from "./utils";
import { download } from "../../../../components/ApplicantDocumentUpload/utils";

export function AustralianNpcIdentificationDocumentsSummaryField({
  field,
}: {
  field: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"];
}) {
  const { data: documentTypes } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<CategoryType, { key: string; value: string; points: number }[]>
  >("/internal/people/document-types/", {
    check: "npc",
  });

  const documents = useMemo(
    () => ({
      primary: field.value.filter(
        ({ document_subtype }) => document_subtype === "primary"
      ),
      secondary: field.value.filter(
        ({ document_subtype }) => document_subtype === "secondary"
      ),
    }),
    [field.value]
  );

  if (!documentTypes) {
    return null;
  }

  const getDocumentTypeLabel = (subtype: CategoryType, key: string) =>
    documentTypes[subtype]?.find((type) => type.key === key)?.value ?? "";

  const totalPoints = getDocumentsPoints(field.value);

  return (
    <>
      {totalPoints < 100 && (
        <SummaryMessage type="warning">
          <Text
            marginTop="space-05"
            style="global/heading-small"
            testID="npc-summary-incomplete"
          >
            <Trans>Identification documents incomplete</Trans>
          </Text>
          <List
            type="unordered"
            size="secondary"
            marginTop="space-1"
            testID="npc-summary-documents-needed"
          >
            <ListItem>
              <Trans>
                Documents provided: {{ totalPoints }} of 100 points provided
              </Trans>
            </ListItem>
          </List>
        </SummaryMessage>
      )}

      <SubHeading>
        <Trans>PRIMARY</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "primary",
          documents.primary[0]?.document_type
        )}
        document={documents.primary[0]}
        onDownload={download}
        testID="npc-identity-documents-summary-primary-1"
      />

      <SubHeading>
        <Trans>PRIMARY (OPTIONAL)</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "primary",
          documents.primary[1]?.document_type
        )}
        document={documents.primary[1]}
        onDownload={download}
        testID="npc-identity-documents-summary-primary-2"
      />

      <SubHeading>
        <Trans>SECONDARY (OPTIONAL)</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "secondary",
          documents.secondary[0]?.document_type
        )}
        document={documents.secondary[0]}
        onDownload={download}
        testID="npc-identity-documents-summary-secondary-1"
      />

      <SubHeading>
        <Trans>SECONDARY (OPTIONAL)</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "secondary",
          documents.secondary[1]?.document_type
        )}
        document={documents.secondary[1]}
        onDownload={download}
        testID="npc-identity-documents-summary-secondary-2"
      />
    </>
  );
}

const DisplayDocument = ({
  label,
  document,
  onDownload,
  testID,
}: {
  label: string;
  document?: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"]["value"][0];
  onDownload: (url: string, fileName: string) => void;
  testID?: string;
}) => {
  const { t } = useTranslation();

  if (!document) {
    return (
      <OuterContentItem
        emptyData={t("No documents provided")}
        testID={`${testID}-no-document`}
      />
    );
  }

  return (
    <>
      <InnerContentItem label={t("Document type")} testID={`${testID}-type`}>
        {label}
      </InnerContentItem>

      <InnerContentItem
        label={t("Filename")}
        icon="download"
        onIconPress={() => onDownload(document.get_url, document.file_name)}
        testID={`${testID}-filename`}
      >
        {document.file_name}
      </InnerContentItem>
    </>
  );
};
